/**
 * plainMeaning.js — iter1081
 *
 * Превод на вътрешните числа (DIL бюджет, NIS, BHI) на човешки език.
 * Един източник за целия интерфейс: числото остава за напредналите, но до
 * него винаги стои изречение, което обикновеният човек разбира без речник.
 *
 * Границите са СЪЩИТЕ като вече използваните в интерфейса, за да не се
 * появят две различни истини:
 *   · DIL бюджет — DilBudgetMeter.jsx: <50% зелено · 50–80% amber · >80% червено
 *   · NIS        — utils/dilTerminology.js: 25 / 45 / 65 / 85
 */

export const TONE = {
  good: { color: "#10B981", emojiFree: true },
  watch: { color: "#F59E0B", emojiFree: true },
  high: { color: "#EF4444", emojiFree: true },
  empty: { color: "#64748B", emojiFree: true },
};

/** Как се отрази днешното хранене — по изразходвания инсулинов бюджет. */
export function dilBudgetMeaning({ consumed, budget, mealCount }) {
  if (!mealCount) {
    return {
      tone: "empty",
      title: "Още не знаем как е минал денят",
      sentence: "Запишете какво изядохте и веднага ще видите как се е отразило.",
      action: "Запишете хранене",
    };
  }
  if (!budget) {
    return {
      tone: "empty",
      title: "Липсва Вашият дневен ориентир",
      sentence: "Допълнете профила си (възраст, тегло, цел), за да сравняваме днешния ден с Вашия ориентир.",
      action: "Допълнете профила",
    };
  }
  const pct = Math.min(200, (consumed / budget) * 100);
  if (pct > 80) {
    return {
      tone: "high",
      title: "Днес храната натовари тялото силно",
      sentence: `Изразходвахте ${Math.round(pct)}% от дневния си ориентир. Следващото хранене да е с повече зеленчуци и белтък и по-малко бързи въглехидрати.`,
      action: "Вижте кое качи най-много",
    };
  }
  if (pct > 50) {
    return {
      tone: "watch",
      title: "Денят е умерен — има накъде",
      sentence: `Изразходвахте ${Math.round(pct)}% от дневния си ориентир. Остава спокойно място за още едно балансирано хранене.`,
      action: "Вижте кое качи най-много",
    };
  }
  return {
    tone: "good",
    title: "Днес храната е лека за тялото",
    sentence: `Изразходвахте само ${Math.round(pct)}% от дневния си ориентир. Ако продължите така, това е денят, който помага.`,
    action: "Вижте днешния ден",
  };
}

/** Кратко изречение за NIS (инсулинов отговор) в проценти. */
export function nisMeaning(nis) {
  const v = Number(nis);
  if (!Number.isFinite(v) || v <= 0) return null;
  if (v < 25) return "Много спокоен инсулинов отговор — тялото почти не се напряга.";
  if (v < 45) return "Спокоен инсулинов отговор — храната се поема плавно.";
  if (v < 65) return "Среден инсулинов отговор — усеща се, но е овладяно.";
  if (v < 85) return "Висок инсулинов отговор — храната иска много инсулин наведнъж.";
  return "Много висок инсулинов отговор — това е най-натоварващият вид хранене.";
}

/** Кратко изречение за обща оценка (0–100, по-високо е по-добре). */
export function scoreMeaning(score) {
  const v = Number(score);
  if (!Number.isFinite(v)) return null;
  if (v >= 80) return "Много добро състояние спрямо Вашите данни.";
  if (v >= 60) return "Добро състояние с малки неща за донастройване.";
  if (v >= 40) return "Средно състояние — има ясни неща за подобряване.";
  return "Има какво да се навакса; започнете с едно нещо, не с всичко.";
}

/** Едно изречение за изразходван бюджет — за реда под измерителя. */
export function budgetLine({ consumed, budget }) {
  if (!budget) return null;
  const left = Math.max(0, budget - consumed);
  const pct = (consumed / budget) * 100;
  if (pct > 80) return `Остава малко (${left.toFixed(0)}) — дръжте следващото хранене леко.`;
  if (pct > 50) return `Остават ${left.toFixed(0)} — стигат за още едно спокойно хранене.`;
  return `Остават ${left.toFixed(0)} — имате спокойно място за деня.`;
}
